import { of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { callApi, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';

export const adminPageApiCalls = (adminPage) => {
  const map = {
    'license-usage': {
      get: getLicenseUsage,
      put: () => of({}),
    },
    'database-connections': {
      get: getDatabaseConnections,
      put: putDatabaseConnections,
    },
    email: {
      get: getEmailSettings,
      put: putEmailSettings,
    },
    logging: {
      get: getLoggingSettings,
      put: putLoggingSettings,
    },
    'license-usage-settings': {
      get: getLicenseUsageSettings,
      put: putLicenseUsageSettings,
    },
    'client-uris': {
      get: getClientUriSettings,
      put: putClientUriSettings,
    },
    'data-integrators': {
      get: getDataIntegratorSettings,
      put: putDataIntegratorSettings,
    },
    notifications: {
      get: getNotificationSettings,
      put: putNotificationSettings,
    },
    reports: {
      get: getReportSettings,
      put: putReportSettings,
    },
    'equipment-drivers': {
      get: getEquipmentDrivers,
      put: () => of({}),
      getIndividual: (id) => getEquipmentDriver(id),
      putIndividual: (id) => putEquipmentDriver(id),
    },
    'ags-export-configuration': {
      get: getAgsExportConfigurationList,
      getIndividual: (id) => getAgsExportConfiguration(id),
      putIndividual: (id) => putAgsExportConfiguration(id),
    },
    'report-configuration': {
      get: () => of({}),
      getIndividual: (id) => getReportConfiguration(id),
      putIndividual: (id) => putReportConfiguration(id),
    },
    'report-configuration-group': {
      get: getReportConfigurationGroupList,
      getIndividual: (id) => getReportConfigurationGroup(id),
      putIndividual: (id) => putReportConfigurationGroup(id),
    },
    'qest-field': {
      get: getQestFieldWorkOrderFieldVisibility,
      put: putQestFieldWorkOrderFieldVisibility,
    },
  };

  const apiCalls = map[adminPage];

  if (apiCalls) {
    return apiCalls;
  } else {
    // blank results for invalid admin pages / no admin sub-page yet selected
    return {
      get: () => of({}),
      put: () => of({}),
    };
  }
};

const getLicenseUsage = (state) =>
  callApi(webApi.admin.license.products.get()).pipe(
    mergeMap((res) => {
      const products = res.Body.Products;
      let { products: selectedProducts, period } = state.admin.syncParams.licenseUsageFilter;

      // If the selection is not initialised, retrieve usage with all licensed products.
      if (selectedProducts === null) {
        selectedProducts = products.map((p) => p.Licensed);
      }

      let chartProducts = products?.filter((_, i) => !!selectedProducts[i]).map((p) => p.Name) ?? [];

      return callApi(webApi.admin.license.usage.get({ Products: chartProducts, PeriodToReview: period })).pipe(
        map((res) => ({
          products,
          usage: res.Body,
        }))
      );
    }),
    catchWithAuthErrors((err) => throwError(err))
  );

const getDatabaseConnections = () =>
  callApi(webApi.admin.settings.databaseConnections.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putDatabaseConnections = (syncData) =>
  callApi(webApi.admin.settings.databaseConnections.put({ Body: syncData })).pipe(
    catchWithAuthErrors((err) => throwError(err))
  );

const getEmailSettings = () =>
  callApi(webApi.admin.settings.email.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putEmailSettings = (syncData) =>
  callApi(webApi.admin.settings.email.put({ Body: syncData })).pipe(catchWithAuthErrors((err) => throwError(err)));

const getLoggingSettings = () =>
  callApi(webApi.admin.settings.logging.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putLoggingSettings = (syncData) =>
  callApi(webApi.admin.settings.logging.put({ Body: syncData })).pipe(catchWithAuthErrors((err) => throwError(err)));

const getLicenseUsageSettings = () =>
  callApi(webApi.admin.settings.license.usage.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putLicenseUsageSettings = (syncData) =>
  callApi(webApi.admin.settings.license.usage.put({ Body: syncData })).pipe(
    catchWithAuthErrors((err) => throwError(err))
  );

const getEquipmentDrivers = () =>
  callApi(webApi.list.equipment.drivers.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const getEquipmentDriver = (id) => () =>
  callApi(webApi.equipment.drivers.get({ driverId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putEquipmentDriver = (id) => (syncData) =>
  callApi(webApi.equipment.drivers.put({ Body: syncData }, { driverId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const getClientUriSettings = () =>
  callApi(webApi.admin.settings.clientUris.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putClientUriSettings = (syncData) =>
  callApi(webApi.admin.settings.clientUris.put({ Body: syncData })).pipe(catchWithAuthErrors((err) => throwError(err)));

const getDataIntegratorSettings = () =>
  callApi(webApi.admin.settings.dataIntegrators.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putDataIntegratorSettings = (syncData) =>
  callApi(webApi.admin.settings.dataIntegrators.put({ Body: syncData })).pipe(
    catchWithAuthErrors((err) => throwError(err))
  );

const getNotificationSettings = () =>
  callApi(webApi.admin.settings.notifications.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putNotificationSettings = (syncData) =>
  callApi(webApi.admin.settings.notifications.put({ Body: syncData })).pipe(
    catchWithAuthErrors((err) => throwError(err))
  );

const getReportSettings = () =>
  callApi(webApi.admin.settings.reports.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putReportSettings = (syncData) =>
  callApi(webApi.admin.settings.reports.put({ Body: syncData })).pipe(catchWithAuthErrors((err) => throwError(err)));

const getAgsExportConfigurationList = () =>
  callApi(webApi.ags.exportConfigurationList.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const getAgsExportConfiguration = (id) => () =>
  callApi(webApi.ags.exportConfiguration.get({ configurationId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putAgsExportConfiguration = (id) => (syncData) =>
  callApi(webApi.ags.exportConfiguration.put({ Body: syncData }, { configurationId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const getQestFieldWorkOrderFieldVisibility = () =>
  callApi(webApi.admin.settings.qestField.workorder.optionalFields.get()).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putQestFieldWorkOrderFieldVisibility = (syncData) =>
  callApi(webApi.admin.settings.qestField.workorder.optionalFields.put({ Body: syncData })).pipe(
    catchWithAuthErrors((err) => throwError(err))
  );

const getReportConfiguration = (id) => () =>
  callApi(webApi.reportConfig.get({ reportConfigurationId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const putReportConfiguration = (id) => (syncData) =>
  callApi(webApi.reportConfig.put({ Body: syncData }, { reportConfigurationId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );

const getReportConfigurationGroupList = () =>
  callApi(webApi.reportConfigGroupList.get()).pipe(
    map((res) => {
      console.log(res);
      return res.Body;
    }),
    catchWithAuthErrors((err) => throwError(err))
  );
const getReportConfigurationGroup = (id) => () =>
  callApi(webApi.reportConfigGroup.get({ groupId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError)
  );
const putReportConfigurationGroup = (id) => (syncData) =>
  callApi(webApi.reportConfigGroup.put({ Body: syncData }, { reportConfigurationId: id })).pipe(
    map((res) => res.Body),
    catchWithAuthErrors((err) => throwError(err))
  );
